import {
  AddRounded,
  BlockRounded,
  CheckCircleRounded,
  DeleteOutlineRounded,
  EditRounded,
  ManageAccountsRounded,
  RefreshRounded,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { type FormEvent, useEffect, useState } from "react";
import {
  createAdminUser,
  deleteAdminUser,
  getAdminProfile,
  getAdminUsers,
  updateAdminProfile,
  updateAdminUser,
  type AdminUser,
} from "../api/auth";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useNotifications } from "../components/NotificationProvider";

const roles: AdminUser["role"][] = [
  "file_viewer",
  "inspector",
  "manager",
  "admin",
];
type UserForm = {
  full_name: string;
  email: string;
  password: string;
  role: AdminUser["role"];
  is_active: boolean;
};
const emptyForm = (): UserForm => ({
  full_name: "",
  email: "",
  password: "",
  role: "file_viewer",
  is_active: true,
});
const formatLastLogin = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Never logged in";

export function AdminUsersPage({
  currentUserId,
}: {
  token: string;
  currentUserId?: number;
}) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userFilter, setUserFilter] = useState<"all" | "admins" | "system">(
    "all",
  );
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [profile, setProfile] = useState({
    full_name: "",
    email: "",
    password: "",
  });
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useNotifications();

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      setUsers(await getAdminUsers());
      setError("");
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : "Unable to load users",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => void loadUsers(), 0);
    return () => window.clearTimeout(timer);
  }, []);

  const openProfile = async () => {
    setProfileOpen(true);
    try {
      const current = await getAdminProfile();
      setProfile({
        full_name: current.full_name,
        email: current.email,
        password: "",
      });
    } catch (profileError) {
      setError(
        profileError instanceof Error
          ? profileError.message
          : "Unable to load profile",
      );
    }
  };

  const saveProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const updated = await updateAdminProfile({
        full_name: profile.full_name.trim(),
        email: profile.email.trim().toLowerCase(),
        password: profile.password || undefined,
      });
      const session = JSON.parse(
        window.localStorage.getItem("hmo-manager-auth") ?? "{}",
      );
      window.localStorage.setItem(
        "hmo-manager-auth",
        JSON.stringify({
          ...session,
          name: updated.full_name,
          email: updated.email,
        }),
      );
      setProfileOpen(false);
      showSnackbar({
        title: "Profile updated",
        message: "Your profile details have been saved.",
        variant: "success",
      });
    } catch (profileError) {
      setError(
        profileError instanceof Error
          ? profileError.message
          : "Unable to update profile",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const saveUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      if (editingUser) {
        await updateAdminUser(editingUser.id, {
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password || undefined,
          role: form.role,
          is_active: form.is_active,
        });
        showSnackbar({
          title: "User updated",
          message: "The account has been updated.",
          variant: "success",
        });
      } else {
        await createAdminUser({
          full_name: form.full_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
        });
        showSnackbar({
          title: "User created",
          message: "The new account is ready to use.",
          variant: "success",
        });
      }
      setCreateOpen(false);
      setEditingUser(null);
      await loadUsers();
    } catch (userError) {
      setError(
        userError instanceof Error ? userError.message : "Unable to save user",
      );
      setIsLoading(false);
    }
  };

  const toggleUser = async (user: AdminUser) => {
    setIsLoading(true);
    try {
      await updateAdminUser(user.id, { is_active: !user.is_active });
      await loadUsers();
    } catch (userError) {
      setError(
        userError instanceof Error
          ? userError.message
          : "Unable to update user",
      );
      setIsLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!deletingUser) return;
    setIsLoading(true);
    try {
      await deleteAdminUser(deletingUser.id);
      setDeletingUser(null);
      await loadUsers();
      showSnackbar({
        title: "User deleted",
        message: "The account has been removed.",
        variant: "info",
      });
    } catch (userError) {
      setError(
        userError instanceof Error
          ? userError.message
          : "Unable to delete user",
      );
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (userFilter === "admins") return user.role === "admin";
    if (userFilter === "system") return user.role !== "admin";
    return true;
  });

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography
              variant="overline"
              color="primary.main"
              sx={{ fontWeight: 700, letterSpacing: ".18em" }}
            >
              Access control
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              User accounts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Create, edit, and manage workspace access.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ManageAccountsRounded />}
              onClick={() => void openProfile()}
            >
              My profile
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => {
                setForm(emptyForm());
                setEditingUser(null);
                setCreateOpen(true);
              }}
            >
              Create user
            </Button>
          </Stack>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Admins and system users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage access without affecting your own account.
            </Typography>
          </Box>
          <Tooltip title="Refresh users">
            <IconButton
              onClick={() => void loadUsers()}
              disabled={isLoading}
              aria-label="Refresh users"
              sx={{ flexShrink: 0, mt: -0.5 }}
            >
              <RefreshRounded />
            </IconButton>
          </Tooltip>
        </Stack>
        <ToggleButtonGroup
          exclusive
          value={userFilter}
          onChange={(_, value) => value && setUserFilter(value)}
          size="small"
          sx={{ mb: 2 }}
          aria-label="Filter user accounts"
        >
          <ToggleButton value="all">All ({users.length})</ToggleButton>
          <ToggleButton value="admins">
            Admins ({users.filter((user) => user.role === "admin").length})
          </ToggleButton>
          <ToggleButton value="system">
            System users ({users.filter((user) => user.role !== "admin").length}
            )
          </ToggleButton>
        </ToggleButtonGroup>
        <Stack spacing={1}>
          {filteredUsers.map((user) => {
            const isSelf = user.id === currentUserId;
            return (
              <Stack
                key={user.id}
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                alignItems={{ sm: "center" }}
                sx={{
                  p: { xs: 1.5, sm: 1.75 },
                  border: "1px solid",
                  borderColor: isSelf ? "primary.main" : "divider",
                  borderRadius: 2,
                }}
              >
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.full_name}
                    {isSelf ? " (you)" : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last login: {formatLastLogin(user.last_login_at)}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flexWrap: "wrap" }}
                >
                  <Chip size="small" label={user.role.replace("_", " ")} />
                  <Chip
                    size="small"
                    color={user.is_active ? "success" : "default"}
                    icon={
                      user.is_active ? <CheckCircleRounded /> : <BlockRounded />
                    }
                    label={user.is_active ? "Active" : "Disabled"}
                  />
                  <Tooltip title="Edit account">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setForm({
                          full_name: user.full_name,
                          email: user.email,
                          password: "",
                          role: user.role,
                          is_active: user.is_active,
                        });
                        setEditingUser(user);
                        setCreateOpen(true);
                      }}
                      aria-label={`Edit ${user.full_name}`}
                    >
                      <EditRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    disabled={isSelf}
                    onClick={() => void toggleUser(user)}
                  >
                    {user.is_active ? "Disable" : "Enable"}
                  </Button>
                  <Tooltip
                    title={
                      isSelf
                        ? "You cannot delete your own account"
                        : "Delete account"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={isSelf}
                        onClick={() => setDeletingUser(user)}
                        aria-label={`Delete ${user.full_name}`}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Paper>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingUser ? "Edit workspace user" : "Create workspace user"}
        </DialogTitle>
        <Stack component="form" onSubmit={saveUser}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Full name"
                required
                value={form.full_name}
                onChange={(event) =>
                  setForm({ ...form, full_name: event.target.value })
                }
              />
              <TextField
                label="Email"
                type="email"
                required
                value={form.email}
                onChange={(event) =>
                  setForm({ ...form, email: event.target.value })
                }
              />
              <TextField
                label={editingUser ? "New password (optional)" : "Password"}
                type="password"
                required={!editingUser}
                inputProps={{ minLength: 8 }}
                value={form.password}
                onChange={(event) =>
                  setForm({ ...form, password: event.target.value })
                }
              />
              <TextField
                select
                label="Role"
                value={form.role}
                onChange={(event) =>
                  setForm({
                    ...form,
                    role: event.target.value as AdminUser["role"],
                  })
                }
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>
              {editingUser && (
                <TextField
                  select
                  label="Status"
                  value={form.is_active ? "active" : "disabled"}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      is_active: event.target.value === "active",
                    })
                  }
                  disabled={editingUser.id === currentUserId}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </TextField>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingUser ? "Save changes" : "Create user"}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>

      <Dialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>My profile</DialogTitle>
        <Stack component="form" onSubmit={saveProfile}>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Full name"
                required
                value={profile.full_name}
                onChange={(event) =>
                  setProfile({ ...profile, full_name: event.target.value })
                }
              />
              <TextField
                label="Email"
                type="email"
                required
                value={profile.email}
                onChange={(event) =>
                  setProfile({ ...profile, email: event.target.value })
                }
              />
              <TextField
                label="New password (optional)"
                type="password"
                inputProps={{ minLength: 8 }}
                value={profile.password}
                onChange={(event) =>
                  setProfile({ ...profile, password: event.target.value })
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProfileOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              Save profile
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
      <ConfirmDialog
        open={Boolean(deletingUser)}
        title="Delete workspace user?"
        message={
          deletingUser
            ? `This permanently removes ${deletingUser.full_name}'s account.`
            : ""
        }
        confirmLabel="Delete account"
        onConfirm={() => void deleteUser()}
        onClose={() => setDeletingUser(null)}
      />
    </Stack>
  );
}
